import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { apiGet, apiPut, apiPost } from "@/lib/api";
import { toast } from "sonner";
import { Save, RefreshCw, Loader2 } from "lucide-react";

export const Route = createFileRoute("/_authenticated/admin/permits")({
  component: AdminPermitsPage,
});

const PERMIT_LABELS: Record<string, string> = {
  hot_work: "Hot Work",
  confined_space: "Confined Space",
  excavation: "Excavation",
  fall_protection: "Fall Protection",
};

const PERMIT_TYPES = [
  "hot_work",
  "confined_space",
  "excavation",
  "fall_protection",
] as const;

function AdminPermitsPage() {
  const queryClient = useQueryClient();
  const [durations, setDurations] = useState<Record<string, number>>({});
  const [initialized, setInitialized] = useState(false);

  const { isLoading } = useQuery({
    queryKey: ["admin", "permits", "durations"],
    queryFn: async () => {
      const result = await apiGet<{ data: Record<string, number> }>(
        "/api/admin/permits/durations"
      );
      if (!initialized) {
        setDurations(result.data);
        setInitialized(true);
      }
      return result;
    },
  });

  const saveMutation = useMutation({
    mutationFn: (body: Record<string, number>) =>
      apiPut<{ data: Record<string, number> }>(
        "/api/admin/permits/durations",
        body
      ),
    onSuccess: (result) => {
      setDurations(result.data);
      queryClient.invalidateQueries({
        queryKey: ["admin", "permits", "durations"],
      });
      toast.success("Permit durations updated");
    },
    onError: (err: Error) => toast.error(err.message),
  });

  if (isLoading) {
    return (
      <div className="space-y-2 py-8">
        <div className="h-4 bg-muted rounded animate-pulse" />
        <div className="h-4 bg-muted rounded animate-pulse w-3/4" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
        Permit Duration Settings
      </h2>
      <p className="text-sm text-muted-foreground">
        Set the default duration (in hours) for each permit type. Permits will
        auto-expire after this duration.
      </p>

      <Card>
        <CardContent className="p-4 space-y-4">
          {PERMIT_TYPES.map((type) => (
            <div key={type} className="flex items-center gap-4">
              <Label className="w-40 text-sm shrink-0">
                {PERMIT_LABELS[type]}
              </Label>
              <Input
                type="number"
                min={1}
                max={72}
                value={durations[type] ?? ""}
                onChange={(e) =>
                  setDurations((prev) => ({
                    ...prev,
                    [type]: parseInt(e.target.value, 10) || 0,
                  }))
                }
                className="h-9 text-sm w-24"
              />
              <span className="text-xs text-muted-foreground">hours</span>
            </div>
          ))}

          <div className="flex justify-end pt-2">
            <Button
              size="sm"
              disabled={saveMutation.isPending}
              onClick={() => saveMutation.mutate(durations)}
            >
              <Save className="h-4 w-4 mr-1" />
              {saveMutation.isPending ? "Saving..." : "Save"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Run Permit Expiry Check */}
      <PermitExpiryCheckCard />
    </div>
  );
}

// ─── Permit Expiry Check Card ───────────────────────────────────────

function PermitExpiryCheckCard() {
  const mutation = useMutation({
    mutationFn: () =>
      apiPost<{ success: boolean; message: string }>(
        "/api/admin/run-permit-expiry"
      ),
    onSuccess: (data) => {
      toast.success(data.message || "Permit expiry check completed");
    },
    onError: (err: Error) => {
      toast.error(err.message || "Failed to run permit expiry check");
    },
  });

  return (
    <Card>
      <CardContent className="p-4 flex items-center justify-between gap-2">
        <div>
          <p className="text-sm font-medium">Permit Expiry Check</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            Manually trigger the permit expiry cron. Auto-expires overdue permits and sends 30-minute warnings.
          </p>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={() => mutation.mutate()}
          disabled={mutation.isPending}
          className="shrink-0 gap-1.5"
        >
          {mutation.isPending ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <RefreshCw className="h-3.5 w-3.5" />
          )}
          {mutation.isPending ? "Running..." : "Run Now"}
        </Button>
      </CardContent>
    </Card>
  );
}
